import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IHasSourceIdentifier,
  IMigratedDataSummary,
  IPracticeMigration,
  ISourceEntityHandler,
  ISourceEntityRecord,
  ISourceEntityTypes,
  ITranslationMap,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Appointment,
  Interaction,
  Appointment as Patient,
  SchedulingEvent,
  TreatmentPlan,
  hasMergeConflicts,
} from '@principle-theorem/principle-core';
import {
  IAppointment,
  IAssociatedTreatment,
  IBrand,
  IFollowUp,
  IInteractionV2,
  IPatient,
  ISchedulingEvent,
  IStaffer,
  ITreatmentPlan,
  ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  FirestoreMigrate,
  INamedDocument,
  IReffable,
  PrimaryKey,
  Timestamp,
  Transaction,
  WithPrimaryKey,
  WithRef,
  asDocRef,
  asReffable,
  getError,
  isObject,
  resolveSequentially,
  runTransaction,
  safeCombineLatest,
  toTimestamp,
} from '@principle-theorem/shared';
import { isString, omit } from 'lodash';
import { Observable, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslationMapHandler } from '../../translation-map';
import { PreloadTranslationMap } from '../../translation-map/preload-translation-map';
import { BaseDestinationEntity } from '../base-destination-entity';
import { DestinationEntity } from '../destination-entity';
import {
  IStepWithIdentifiers,
  ITreatmentPlanStepPair,
  PATIENT_TREATMENT_PLAN_CUSTOM_MAPPING_TYPE,
  PATIENT_TREATMENT_STEP_CUSTOM_MAPPING_TYPE,
} from './patient-treatment-plans';
import { toTextContent, initVersionedSchema } from '@principle-theorem/editor';

export const PATIENT_APPOINTMENT_RESOURCE_TYPE = 'patientAppointment';

export const PATIENT_APPOINTMENT_FOLLOW_UP_RESOURCE_TYPE =
  'appointmentFollowUp';

export const PATIENT_APPOINTMENT_INTERACTION_RESOURCE_TYPE =
  'appointmentInteraction';

export const PATIENT_APPOINTMENT_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_APPOINTMENT_RESOURCE_TYPE,
    label: 'Patient Appointments',
    description: '',
  },
});

export interface IAppointmentSuccessData {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  patientRef: DocumentReference<IPatient>;
  appointmentRefs: DocumentReference<IAppointment>[];
}

export interface IPatientAppointmentJobData<
  PatientRecord extends ISourceEntityTypes,
> {
  sourcePatient: IGetRecordResponse<PatientRecord>;
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  practitioners: INamedDocument<IStaffer>[];
  brand: INamedDocument<IBrand>;
}

export interface IPatientAppointmentBuildData {
  appointmentUid: string;
  appointment: Omit<IAppointment, 'treatmentPlan' | 'invoiceRef'> &
    Partial<Pick<IAppointment, 'treatmentPlan'>>;
  followUp?: IFollowUp;
  createdAt?: Timestamp;
  planUid: string;
  stepUid: string;
  interactions: IInteractionV2[];
  schedulingEvent?: ISchedulingEvent;
}

export interface IPatientAppointmentMigrationData {
  patientRef: DocumentReference<IPatient>;
  sourcePatientId: string;
  appointments: IPatientAppointmentBuildData[];
  planPairs: ITreatmentPlanStepPair[];
}

interface IPlanStepPairsWithRef {
  plan: IReffable<ITreatmentPlan> & ITreatmentPlan & IHasSourceIdentifier;
  steps: (IReffable<ITreatmentStep> & IStepWithIdentifiers)[];
}

export abstract class BasePatientAppointmentDestinationEntity<
  PatientRecord extends ISourceEntityTypes,
  JobData extends IPatientAppointmentJobData<PatientRecord>,
> extends BaseDestinationEntity<
  IAppointmentSuccessData,
  JobData,
  IPatientAppointmentMigrationData
> {
  abstract patientSourceEntity: ISourceEntityHandler<PatientRecord>;

  get sourceCountComparison(): ISourceEntityHandler<PatientRecord> {
    return this.patientSourceEntity;
  }

  sourceCountDataAccessor(data: JobData): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getDestinationEntityRecordUid(data: JobData): string {
    return data.sourcePatient.record.uid;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<IAppointmentSuccessData>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return combineLatest([
      Firestore.getDoc(record.data.patientRef),
      safeCombineLatest(
        record.data.appointmentRefs.map((appointmentRef) =>
          Firestore.getDoc(appointmentRef)
        )
      ),
    ]).pipe(
      map(([patient, appointments]) => {
        const data: IMigratedDataSummary[] = [
          {
            label: 'Patient',
            data: patient,
          },
        ];

        data.push(
          ...appointments.map((appointment) => ({
            label: 'Appointments',
            data: appointment,
          }))
        );

        return data;
      })
    );
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: IPatientAppointmentMigrationData
  ): Promise<IPatientAppointmentMigrationData | undefined> {
    const existingAppointments: IPatientAppointmentBuildData[] = [];

    const appointmentMergeConflicts = await resolveSequentially(
      data.appointments,
      async (appointment) => {
        const appointmentRef = await translationMap.getDestination(
          appointment.appointmentUid,
          PATIENT_APPOINTMENT_RESOURCE_TYPE
        );

        try {
          const existingAppointment = appointmentRef
            ? await Firestore.getDoc(asDocRef<IAppointment>(appointmentRef))
            : undefined;

          if (!existingAppointment) {
            return;
          }

          const builtData: IPatientAppointmentBuildData = {
            ...appointment,
            appointment: {
              ...existingAppointment,
            },
          };

          existingAppointments.push(builtData);

          return hasMergeConflicts(
            appointment.appointment,
            omit(existingAppointment, 'treatmentPlan'),
            ['dateFrom', 'dateTo'],
            [
              {
                key: 'tags',
                typeGuardFn: (item): item is unknown =>
                  isObject(item) && isString(item.name),
                sortByPath: 'name',
              },
            ]
          );
        } catch {
          return false;
        }
      }
    );

    if (appointmentMergeConflicts.some((mergeConflict) => mergeConflict)) {
      return {
        ...data,
        appointments: existingAppointments,
      };
    }
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: JobData,
    _migrationData: IPatientAppointmentMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: JobData,
    migrationData: IPatientAppointmentMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const treatmentPlanRefs = await runTransaction(async (transaction) =>
        this._upsertAppointmentPlanPairs(
          migrationData.planPairs,
          migrationData.patientRef,
          translationMap,
          transaction
        )
      );

      const appointmentRefs = await resolveSequentially(
        migrationData.appointments,
        async (appointmentData) => {
          if (!appointmentData.appointment.treatmentPlan) {
            const planStepPair = treatmentPlanRefs.find(
              (pair) => pair.plan.sourceIdentifier === appointmentData.planUid
            );
            if (!planStepPair) {
              throw new Error(
                `No treatment plan found for appointment ${appointmentData.appointmentUid}`
              );
            }
            const step = planStepPair.steps.find(
              (planStep) =>
                planStep.sourceIdentifier === appointmentData.stepUid
            );
            if (!step) {
              throw new Error(
                `No treatment step found for appointment ${appointmentData.appointmentUid}`
              );
            }
            appointmentData.appointment.treatmentPlan =
              TreatmentPlan.treatmentStepToAssociatedTreatment(
                { ...planStepPair.plan, ref: planStepPair.plan.ref },
                { ...step, ref: step.ref }
              );
          }

          return runTransaction(async (transaction) => {
            const appointmentRef = await this._upsertAppointment(
              migrationData.patientRef,
              appointmentData,
              translationMap,
              transaction
            );

            const followUp = appointmentData.followUp;
            if (followUp) {
              const followUpRef = await PreloadTranslationMap.preload(
                translationMap,
                PATIENT_APPOINTMENT_FOLLOW_UP_RESOURCE_TYPE,
                Appointment.followUpCol(asReffable(appointmentRef)),
                followUp.uid
              );

              await FirestoreMigrate.upsertDoc(
                Appointment.followUpCol(asReffable(appointmentRef)),
                followUp,
                followUpRef,
                transaction
              );
            }

            const schedulingEvent = appointmentData.schedulingEvent;
            if (schedulingEvent) {
              const schedulingEventRef = await FirestoreMigrate.upsertDoc(
                Appointment.schedulingEventCol({ ref: appointmentRef }),
                schedulingEvent,
                `scheduling-event-${appointmentData.appointmentUid}`,
                transaction
              );
              appointmentData.interactions.push(
                Interaction.init({
                  uid: `scheduling-event-interaction-${appointmentData.appointmentUid}`,
                  title: [toTextContent(`Appointment marked FTA by Migration`)],
                  type: SchedulingEvent.getInteractionType(schedulingEvent),
                  content: initVersionedSchema(),
                  schedulingEvent: schedulingEventRef,
                  createdAt: appointmentData.appointment.event?.from,
                })
              );
            }

            await resolveSequentially(
              appointmentData.interactions,
              async (interaction) => {
                const interactionRef = await PreloadTranslationMap.preload(
                  translationMap,
                  PATIENT_APPOINTMENT_INTERACTION_RESOURCE_TYPE,
                  Appointment.interactionCol(asReffable(appointmentRef)),
                  `appointment-${appointmentData.appointmentUid}-interaction-${interaction.uid}`
                );

                return FirestoreMigrate.upsertDoc(
                  Appointment.interactionCol(asReffable(appointmentRef)),
                  interaction,
                  interactionRef,
                  transaction
                );
              }
            );

            return appointmentRef;
          });
        }
      );

      return this._buildSuccessResponse(
        jobData.sourcePatient,
        migrationData.patientRef,
        appointmentRefs
      );
    } catch (error) {
      return this._buildErrorResponse(jobData.sourcePatient, getError(error));
    }
  }

  buildSkippedResponse(
    data: JobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  protected _buildSuccessResponse(
    patient: IGetRecordResponse<PatientRecord>,
    patientRef: DocumentReference<IPatient>,
    appointmentRefs: DocumentReference<IAppointment>[]
  ): IDestinationEntityRecord<IAppointmentSuccessData> {
    return {
      uid: patient.record.uid,
      label: patient.record.label,
      data: {
        sourceRef: patient.record.id,
        patientRef,
        appointmentRefs,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: patient.record.id,
      migratedAt: toTimestamp(),
    };
  }

  protected _buildErrorResponse(
    patient: IGetRecordResponse<PatientRecord>,
    errorMessage?: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: patient.record.uid,
      label: patient.record.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: patient.record.id,
      errorMessage: errorMessage ?? 'Missing required properties for patient',
      failData: {
        patientRef: patient.record.id,
      },
    };
  }

  private async _upsertAppointmentPlanPairs(
    planPairs: ITreatmentPlanStepPair[],
    patientRef: DocumentReference<IPatient>,
    translationMap: TranslationMapHandler,
    transaction: Transaction
  ): Promise<IPlanStepPairsWithRef[]> {
    return resolveSequentially(planPairs, async ({ plan, steps }) => {
      const treatmentPlanCol = TreatmentPlan.col(asReffable(patientRef));
      const treatmentPlanRef = await PreloadTranslationMap.preload(
        translationMap,
        PATIENT_TREATMENT_PLAN_CUSTOM_MAPPING_TYPE,
        treatmentPlanCol,
        plan.sourceIdentifier
      );

      await FirestoreMigrate.upsertDoc(
        TreatmentPlan.col({
          ref: patientRef,
        }),
        {
          ...plan,
          deleted: false,
        },
        treatmentPlanRef,
        transaction
      );

      const stepRefs = await resolveSequentially(steps, async (step) => {
        const treatmentStepCol = TreatmentPlan.treatmentStepCol(
          asReffable(treatmentPlanRef)
        );
        const stepDestinationRef = await PreloadTranslationMap.preload(
          translationMap,
          PATIENT_TREATMENT_STEP_CUSTOM_MAPPING_TYPE,
          treatmentStepCol,
          step.sourceIdentifier
        );

        const stepRef = await FirestoreMigrate.upsertDoc(
          treatmentStepCol,
          {
            ...step,
            deleted: false,
          },
          stepDestinationRef,
          transaction
        );

        return { ...step, ref: stepRef };
      });

      return {
        plan: { ...plan, ref: treatmentPlanRef },
        steps: stepRefs,
      };
    });
  }

  private async _upsertAppointment(
    patientRef: DocumentReference<IPatient>,
    data: IPatientAppointmentBuildData,
    translationMap: TranslationMapHandler,
    transaction: Transaction
  ): Promise<DocumentReference<IAppointment>> {
    const appointmentRef = await PreloadTranslationMap.preload(
      translationMap,
      PATIENT_APPOINTMENT_RESOURCE_TYPE,
      Appointment.col(asReffable(patientRef)),
      data.appointmentUid
    );

    const appointment: IAppointment = {
      ...data.appointment,
      treatmentPlan: await this._upsertAppointmentPlanStepPairMap(
        translationMap,
        patientRef,
        data,
        transaction
      ),
      createdAt: data.createdAt ?? toTimestamp(),
      deleted: false,
    };

    await FirestoreMigrate.upsertDoc(
      Patient.col({
        ref: patientRef,
      }),
      appointment,
      appointmentRef,
      transaction
    );

    await FirestoreMigrate.patchDoc(
      appointment.treatmentPlan.treatmentStep.ref,
      {
        appointment: appointmentRef,
      },
      undefined,
      transaction
    );
    return appointmentRef;
  }

  private async _upsertAppointmentPlanStepPairMap(
    translationMap: TranslationMapHandler,
    _patientRef: DocumentReference<IPatient>,
    data: IPatientAppointmentBuildData,
    _transaction: Transaction
  ): Promise<IAssociatedTreatment> {
    const treatmentPlan = data.appointment.treatmentPlan;
    if (!treatmentPlan) {
      throw new Error('Appointment is missing treatment plan data');
    }

    await translationMap.upsert({
      sourceIdentifier: `appointment-${data.appointmentUid}`,
      destinationIdentifier: treatmentPlan.ref,
      resourceType: PATIENT_TREATMENT_PLAN_CUSTOM_MAPPING_TYPE,
    });

    await translationMap.upsert({
      sourceIdentifier: `appointment-${data.appointmentUid}`,
      destinationIdentifier: treatmentPlan.treatmentStep.ref,
      resourceType: PATIENT_TREATMENT_STEP_CUSTOM_MAPPING_TYPE,
    });

    return treatmentPlan;
  }
}
